import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/services/supabase';
import { Send, Image as ImageIcon, Video as VideoIcon, Mic, MapPin, Loader2 } from 'lucide-react';
import { useSettingsStore, chipCls } from '@/stores/settings-store';

interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  type: 'text' | 'image' | 'video' | 'audio' | 'location' | 'system';
  body: string | null;
  file_url: string | null;
  thumbnail_url: string | null;
  mime_type: string | null;
  created_at: string;
  sender?: { full_name: string | null; role: string | null } | null;
}

interface Props {
  ticketId: string;
  /** Auth user id of the operator (sender). */
  operatorId: string | null;
}

/**
 * Resolve a ticket_messages.file_url value to a viewable HTTPS URL.
 * - If the value already looks like a full URL, return it as-is (legacy).
 * - Otherwise treat it as a storage path inside the `evidence` bucket and
 *   sign it for short-lived browser access (RLS bucket).
 */
async function resolveMediaUrl(value: string | null): Promise<string | null> {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const { data, error } = await supabase.storage
    .from('evidence')
    .createSignedUrl(value, 60 * 60); // 1h
  if (error) {
    console.warn('[TicketChatPanel] sign url failed', value, error.message);
    return null;
  }
  return data?.signedUrl ?? null;
}

/**
 * Realtime chat panel for an emergency ticket.
 * - Renders text/image/video/audio messages.
 * - Subscribes to ticket_messages INSERT events for the ticket.
 * - Lets the operator send a text reply.
 */
export function TicketChatPanel({ ticketId, operatorId }: Props) {
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState('');
  
  // Quick replies filtered for emergency chats
  const quickReplies = useSettingsStore((s) =>
    s.quickReplies
      .filter((r) => 
        r.modals === 'all' || 
        r.modals === 'emergency' || 
        r.modals === 'sos' || 
        r.modals === 'call'
      )
      .sort((a, b) => a.order - b.order)
  );
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Sign media URLs for messages that have a file_url ─────────────────────
  useEffect(() => {
    const pending = messages.filter(
      (m) => m.file_url && !signedUrls[m.id] && m.type !== 'text' && m.type !== 'system' && m.type !== 'location',
    );
    if (pending.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries: Array<[string, string]> = [];
      for (const m of pending) {
        const url = await resolveMediaUrl(m.file_url);
        if (url) entries.push([m.id, url]);
      }
      if (cancelled || entries.length === 0) return;
      setSignedUrls((prev) => {
        const next = { ...prev };
        for (const [k, v] of entries) next[k] = v;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [messages, signedUrls]);

  // ── Fetch + subscribe ─────────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    setLoading(true);

    async function load() {
      const { data } = await supabase
        .from('ticket_messages')
        .select(
          'id, ticket_id, sender_id, type, body, file_url, thumbnail_url, mime_type, created_at, sender:profiles!ticket_messages_sender_id_fkey(full_name, role)',
        )
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true })
        .limit(200);
      if (!active) return;
      setMessages((data ?? []) as unknown as TicketMessage[]);
      setLoading(false);
    }
    load();

    const ch = supabase
      .channel(`ticket-chat-${ticketId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'ticket_messages', filter: `ticket_id=eq.${ticketId}` },
        async (payload) => {
          const next = payload.new as TicketMessage;
          // Hydrate sender name in a follow-up query (realtime payload is row-only).
          const { data: prof } = await supabase
            .from('profiles')
            .select('full_name, role')
            .eq('id', next.sender_id)
            .maybeSingle();
          setMessages((prev) =>
            prev.some((m) => m.id === next.id) ? prev : [...prev, { ...next, sender: prof ?? null }],
          );
        },
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(ch);
    };
  }, [ticketId]);

  // Auto-scroll to bottom on new messages or after media resolves.
  useEffect(() => {
    if (scrollRef.current) {
      // Defer to next frame so freshly rendered media has measured.
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      });
    }
  }, [messages.length, signedUrls]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || !operatorId || sending) return;
    setSending(true);
    setDraft('');
    const { error } = await supabase.from('ticket_messages').insert({
      ticket_id: ticketId,
      sender_id: operatorId,
      type: 'text',
      body,
    });
    if (error) {
      setDraft(body); // restore so user can retry
      console.warn('[TicketChatPanel] send failed', error.message);
    }
    setSending(false);
  };

  const empty = useMemo(() => !loading && messages.length === 0, [loading, messages.length]);

  return (
    <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl flex flex-col" style={{ minHeight: 320, maxHeight: 480 }}>
      <div className="px-4 pt-4 pb-2 border-b border-gray-200 dark:border-slate-700/60">
        <h4 className="text-gray-600 dark:text-slate-400 text-sm font-medium uppercase">Mensajes con el cliente</h4>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-6 text-gray-500 dark:text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cargando…
          </div>
        )}
        {empty && (
          <p className="text-gray-500 dark:text-slate-500 text-sm text-center py-6">
            Sin mensajes todavía. Envía uno para iniciar la conversación.
          </p>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            message={m}
            isOwn={m.sender_id === operatorId}
            mediaUrl={signedUrls[m.id] ?? null}
          />
        ))}
      </div>

      {/* Composer */}
      <div className="border-t border-gray-200 dark:border-slate-700/60">
        {/* Quick Reply Chips */}
        {quickReplies.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-3 pt-3 pb-2 border-b border-gray-200 dark:border-slate-700/60">
            {quickReplies.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setDraft(r.text)}
                className={`px-2.5 py-0.5 text-xs rounded-full border transition-colors ${chipCls(r.color)}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
        
        {/* Message Input */}
        <div className="p-3 flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Escribe un mensaje…"
          rows={1}
          className="flex-1 resize-none bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white text-sm px-3 py-2 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 max-h-32"
        />
        <button
          onClick={handleSend}
          disabled={!draft.trim() || sending || !operatorId}
          className="h-9 w-9 flex items-center justify-center bg-blue-600 hover:bg-blue-500 rounded-lg text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Enviar"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  isOwn,
  mediaUrl,
}: {
  message: TicketMessage;
  isOwn: boolean;
  mediaUrl: string | null;
}) {
  const time = new Date(message.created_at).toLocaleTimeString('es-GT', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const ROLE_LABEL: Record<string, string> = {
    client:      'Cliente',
    patrol:      'Patrullero',
    operator:    'Operador',
    supervisor:  'Supervisor',
    admin:       'Admin',
    super_admin: 'Super Admin',
  };
  const roleStr = ROLE_LABEL[message.sender?.role ?? ''] ?? 'Usuario';
  const senderLabel = isOwn
    ? 'Tú'
    : `${message.sender?.full_name ?? roleStr} · ${roleStr}`;

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${
          isOwn
            ? 'bg-blue-600 text-white rounded-br-sm'
            : 'bg-gray-200 dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-bl-sm'
        }`}
      >
        {!isOwn && (
          <p className="text-[10px] uppercase tracking-wider opacity-70 mb-1">{senderLabel}</p>
        )}
        <MessageContent message={message} mediaUrl={mediaUrl} />
        <p className={`text-[10px] ${isOwn ? 'text-blue-100/80' : 'text-gray-600 dark:text-slate-400'} mt-1 text-right`}>
          {time}
        </p>
      </div>
    </div>
  );
}

function MessageContent({ message, mediaUrl }: { message: TicketMessage; mediaUrl: string | null }) {
  // Media types render only once we have a signed URL; otherwise show a small loader.
  const needsMedia = ['image', 'video', 'audio'].includes(message.type);
  if (needsMedia && !mediaUrl && message.file_url) {
    return (
      <span className="flex items-center gap-1 opacity-70">
        <Loader2 className="w-3 h-3 animate-spin" /> Cargando…
      </span>
    );
  }
  switch (message.type) {
    case 'text':
      return <p className="whitespace-pre-wrap break-words">{message.body}</p>;
    case 'image':
      return mediaUrl ? (
        <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={mediaUrl}
            alt="Foto"
            className="rounded-lg max-w-full max-h-60 object-cover"
            loading="lazy"
          />
        </a>
      ) : (
        <span className="flex items-center gap-1 opacity-80">
          <ImageIcon className="w-3 h-3" /> Imagen no disponible
        </span>
      );
    case 'video':
      return mediaUrl ? (
        <video src={mediaUrl} controls className="rounded-lg max-w-full max-h-60" preload="metadata" />
      ) : (
        <span className="flex items-center gap-1 opacity-80">
          <VideoIcon className="w-3 h-3" /> Video no disponible
        </span>
      );
    case 'audio':
      return mediaUrl ? (
        <audio src={mediaUrl} controls className="max-w-full" />
      ) : (
        <span className="flex items-center gap-1 opacity-80">
          <Mic className="w-3 h-3" /> Audio no disponible
        </span>
      );
    case 'location':
      return (
        <span className="flex items-center gap-1 opacity-90">
          <MapPin className="w-3 h-3" /> {message.body ?? 'Ubicación compartida'}
        </span>
      );
    case 'system':
      return <p className="italic opacity-80">{message.body}</p>;
    default:
      return <p className="opacity-80">{message.body ?? '[mensaje no soportado]'}</p>;
  }
}
